import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Inbox as InboxIcon, Users, Undo2, PackageCheck, Check } from "lucide-react";
import { getEntries, updateEntry, markSeen } from "../lib/api";
import { deleteWithUndo } from "../lib/items";
import { useAuth } from "../contexts/AuthContext";
import { TypeBadge, DomainBadge, UrgencyBadge, StateBadge, NewBadge, ReviewBadge } from "../components/Badges";
import QuickEdit from "../components/QuickEdit";
import EditableCue from "../components/EditableCue";
import ItemDetailSheet from "../components/ItemDetailSheet";
import { InboxSkeleton } from "../components/Skeleton";
import ReviewNudgeChip from "../components/ReviewNudgeChip";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { fmtDateTime } from "../lib/meta";
import { isOverdue, isDueToday, relativeDue } from "../lib/resurface";
import { inboxMineSections, isShared } from "../lib/browse";

function EntryRow({ entry, onChange, onCueApply, due, isNew, onView, onOpen, onDelete, onHandoff, snoozable, handed }) {
  const muted = entry.status !== "ACTIVE";
  const done = entry.status !== "ACTIVE";
  const overdue = due === "overdue";
  const dueToday = due === "today";
  const amberTreat = overdue || dueToday;

  // One primary status chip. Overdue is never suppressed: it rides as the card
  // treatment (amber title + 3px amber left bar). Handed-item state is carried by
  // the sender-facing confirmation line (Pass 2), not a Seen/progress badge.
  let statusChip = null;
  if (isNew) statusChip = <NewBadge />;
  else if (overdue) statusChip = <UrgencyBadge urgency="OVERDUE" />;
  else if (dueToday) statusChip = <UrgencyBadge urgency="TODAY" />;
  else if (done) statusChip = <StateBadge state="done" />;
  else if (entry.needs_review) statusChip = <ReviewBadge />;

  return (
    <div
      className={`heldall-card bg-[var(--surface)] border border-[var(--line)] p-4 md:p-5 transition-all duration-200 hover:-translate-y-[1px] hover:shadow-[0_8px_28px_rgba(44,40,37,0.09)] ${
        amberTreat ? "border-l-[3px] border-l-[var(--amber)]" : handed ? "border-l-[3px] border-l-[var(--pine)]" : ""
      } ${muted ? "opacity-55" : ""}`}
      data-testid={`inbox-item-${entry.id}`}
    >
      <div className="flex flex-col md:flex-row md:items-start gap-2.5 md:gap-6">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            data-testid={`open-detail-${entry.id}`}
            onClick={() => { onView && onView(entry.id); onOpen && onOpen(entry.id); }}
            className="block w-full text-left group/title"
          >
            <p className={`text-[15px] md:text-base font-medium leading-snug group-hover/title:underline decoration-[var(--pine-line)] underline-offset-2 ${
              entry.status === "DONE" ? "line-through text-[var(--ink4)]" : amberTreat ? "text-[var(--amber-text)]" : "text-[var(--ink)]"
            }`}>
              {entry.interpreted_summary}
            </p>
            <p className="text-[13px] text-[var(--ink3)] mt-1 truncate">“{entry.raw_text}”</p>
          </button>
          {entry.handoff_note_line && (
            <p className="text-[13px] text-[var(--slate)] mt-1.5 flex items-start gap-1.5" data-testid={`inbox-handoff-note-${entry.id}`}>
              <Undo2 size={13} strokeWidth={1.9} className="mt-0.5 shrink-0" /> {entry.handoff_note_line}
            </p>
          )}
          {entry.confirmation_line && (
            <p className="text-[13px] text-[var(--pine)] mt-1.5 flex items-start gap-1.5" data-testid={`inbox-confirmation-${entry.id}`}>
              {entry.status === "DONE"
                ? <Check size={13} strokeWidth={2.4} className="mt-0.5 shrink-0" />
                : <PackageCheck size={13} strokeWidth={1.9} className="mt-0.5 shrink-0" />} {entry.confirmation_line}
            </p>
          )}
          <div className="mt-3">
            <EditableCue entry={entry} onApply={(iso) => onCueApply(entry.id, iso)} testid={`inbox-cue-${entry.id}`} />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 md:justify-end shrink-0 md:max-w-[46%]">
          {statusChip}
          <TypeBadge type={entry.type} subtle />
          <DomainBadge domain={entry.domain} subtle />
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-[var(--line2)]">
        <QuickEdit
          entry={entry}
          onChange={onChange}
          onView={() => onView && onView(entry.id)}
          onSnooze={(iso) => onCueApply(entry.id, iso)}
          onDelete={onDelete}
          onHandoff={onHandoff}
          snoozable={snoozable && entry.status === "ACTIVE"}
        />
      </div>
    </div>
  );
}

export default function Inbox() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const partnerName = user?.partner?.name || "Partner";
  const hasPartner = Boolean(user?.partner);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("mine");
  const [detailId, setDetailId] = useState(null);
  const [handoffInit, setHandoffInit] = useState(null);
  const openHandoff = useCallback((id, action) => { setHandoffInit(action); setDetailId(id); }, []);
  const reviewCount = entries.filter((e) => e.needs_review && e.status === "ACTIVE").length;

  const load = useCallback(async () => {
    try {
      setEntries(await getEntries());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep-link from a handoff-completion push (/inbox?item=<id>): open that item's detail
  // once entries are loaded, then strip the param so back/revisit doesn't re-open it.
  useEffect(() => {
    if (loading) return;
    const itemId = new URLSearchParams(window.location.search).get("item");
    if (itemId && entries.some((e) => e.id === itemId)) {
      setDetailId(itemId);
      navigate("/inbox", { replace: true });
    }
  }, [loading, entries, navigate]);

  // Clear the assignee "New" signal only on a deliberate interaction
  // (opening the card's Adjust panel, or Mark done / Archive) — never just on page land.
  const markItemSeen = useCallback((id) => {
    setEntries((es) => es.map((e) => (e.id === id && e.unseen ? { ...e, unseen: false } : e)));
    markSeen([id]).catch(() => {});
  }, []);

  const handleChange = async (id, changes) => {
    const prev = entries;
    markItemSeen(id);
    setEntries((es) => es.map((e) => (e.id === id ? { ...e, ...changes } : e)));
    try {
      const updated = await updateEntry(id, changes);
      setEntries((es) => es.map((e) => (e.id === id ? updated : e)));
      toast.success("Saved.");
    } catch (_) {
      setEntries(prev);
      toast.error("Couldn't save that change.");
    }
  };

  const handleCue = (id, iso) => {
    const tz = -new Date().getTimezoneOffset();
    return handleChange(id, iso === null
      ? { clear_cue: true, tz_offset: tz }
      : { suggested_datetime: iso, tz_offset: tz });
  };

  const handleDelete = (id) => {
    setDetailId(null);
    deleteWithUndo(id, {
      onDeleted: () => setEntries((es) => es.filter((e) => e.id !== id)),
      onRestored: load,
    });
  };

  const active = entries.filter((e) => e.status === "ACTIVE");
  const completed = entries.filter((e) => e.status !== "ACTIVE");

  // --- My Inbox: the viewer's OWN household items (items they hold), not the whole household ---
  const mineActive = active.filter((e) => e.owner === "me");
  const mineCompleted = completed.filter((e) => e.owner === "me");

  const overdue = mineActive.filter(isOverdue);
  const dueToday = mineActive.filter((e) => !isOverdue(e) && isDueToday(e));

  // --- Calm pile: My Inbox grouped into a few held sections (mutually exclusive by priority).
  // Today (time-critical) wins, then freshly handed items, then everything else held quietly
  // for later. Upcoming / time-anchored items are NOT shown here — Ahead owns "what's coming up".
  const dueFor = (e) => (isOverdue(e) ? "overdue" : isDueToday(e) ? "today" : undefined);
  const { today: todayItems, handed: handedItems, later: laterItems } = inboxMineSections(mineActive);
  const handedLabel = user?.partner?.name ? `From ${user.partner.name}` : "Handed to you";

  // --- Shared mode: items relevant to shared/household responsibility ---
  const dueOf = (e) => (isOverdue(e) ? "overdue" : isDueToday(e) ? "today" : undefined);
  const sharedActive = active.filter(isShared);
  const partnerOwned = sharedActive.filter((e) => e.owner === "partner");
  const household = sharedActive.filter((e) => e.owner !== "partner");
  const sharedCompleted = completed.filter(isShared);
  const sharedCount = sharedActive.length;

  const renderList = (items) => (
    <div className="space-y-3">
      {items.map((e) => (
        <EntryRow key={e.id} entry={e} onChange={handleChange} onCueApply={handleCue} due={dueOf(e)} isNew={e.unseen} onView={markItemSeen} onOpen={setDetailId} onDelete={handleDelete} onHandoff={openHandoff} />
      ))}
    </div>
  );

  const newItems = mineActive.filter((e) => e.unseen);
  const newCount = newItems.length;

  // The handoff banner is a calm shortcut, not an alert. One handed item -> open it directly;
  // several -> focus the first in place (no new handoff centre, no surveillance language).
  const openHandedItems = useCallback(() => {
    if (newItems.length === 0) return;
    const first = newItems[0];
    if (newItems.length === 1) {
      markItemSeen(first.id);
      setDetailId(first.id);
      return;
    }
    const el = document.querySelector(`[data-testid="inbox-item-${first.id}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("handoff-highlight");
      setTimeout(() => el.classList.remove("handoff-highlight"), 1700);
    }
  }, [newItems, markItemSeen]);

  const tabCls = (active_) =>
    `inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all duration-200 ${
      active_ ? "bg-[var(--surface)] text-[var(--ink)] shadow-[0_2px_8px_rgba(0,0,0,0.06)]" : "text-[var(--ink3)] hover:text-[var(--ink2)]"
    }`;

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-8 md:py-14">
      <div className="mb-6 md:mb-7">
        <h1 className="font-display text-3xl md:text-4xl tracking-tight font-medium text-[var(--ink)]">Inbox</h1>
        <p className="text-[var(--ink3)] mt-2 text-sm md:text-base">Things waiting for a quick check, or held for later.</p>
        {reviewCount > 0 && (
          <div className="mt-3.5">
            <ReviewNudgeChip count={reviewCount} fullWidth onClick={() => navigate("/?review=1")} />
          </div>
        )}
      </div>

      <div className="inline-flex items-center gap-1 rounded-full bg-[var(--line2)] p-1 mb-8" data-testid="inbox-tabs">
        <button data-testid="inbox-tab-mine" className={tabCls(view === "mine")} onClick={() => setView("mine")}>
          My Inbox
        </button>
        <button data-testid="inbox-tab-shared" className={tabCls(view === "shared")} onClick={() => setView("shared")}>
          <Users size={15} strokeWidth={1.9} />
          Shared
          {hasPartner && sharedCount > 0 && (
            <span className="text-xs font-bold text-[var(--pine)] bg-[var(--pine-soft)] rounded-full px-1.5 py-0.5" data-testid="shared-count">
              {sharedCount}
            </span>
          )}
        </button>
      </div>

      {loading ? (
        <InboxSkeleton />
      ) : view === "shared" ? (
        sharedActive.length === 0 && sharedCompleted.length === 0 ? (
          <div className="rounded-2xl bg-[var(--surface)] border border-[var(--line)] p-12 text-center" data-testid="shared-empty">
            <Users className="mx-auto text-[var(--pine-line)] mb-3" size={30} strokeWidth={1.4} />
            <p className="text-[var(--ink3)] max-w-sm mx-auto leading-relaxed">
              Nothing shared yet. Invite someone and items you share will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-10" data-testid="shared-view">
            <p className="text-sm text-[var(--ink3)] -mt-3">
              Everything you're handling together, in one place.
            </p>
            {household.length > 0 && (
              <section data-testid="shared-section-household">
                <div className="flex items-baseline gap-3 mb-4">
                  <h2 className="text-xl font-medium text-[var(--pine)]">Shared / Household</h2>
                  <span className="text-xs font-bold text-[var(--pine)] bg-[var(--pine-soft)] rounded-full px-2 py-0.5">{household.length}</span>
                  <span className="text-sm text-[var(--ink4)] hidden md:inline">{hasPartner ? "Visible to everyone in your household." : "Shared with your partner once someone joins."}</span>
                </div>
                {renderList(household)}
              </section>
            )}
            {partnerOwned.length > 0 && (
              <section data-testid="shared-section-partner">
                <div className="flex items-baseline gap-3 mb-4">
                  <h2 className="text-xl font-medium text-[var(--slate)]">{partnerName}'s plate</h2>
                  <span className="text-xs font-bold text-[var(--slate)] bg-[var(--slate-soft)] rounded-full px-2 py-0.5">{partnerOwned.length}</span>
                  <span className="text-sm text-[var(--ink4)] hidden md:inline">On {partnerName}'s plate.</span>
                </div>
                {renderList(partnerOwned)}
              </section>
            )}
            {sharedCompleted.length > 0 && (
              <section data-testid="shared-section-done">
                <h2 className="text-xl font-medium text-[var(--ink)] mb-4">Done</h2>
                {renderList(sharedCompleted)}
              </section>
            )}
          </div>
        )
      ) : mineActive.length === 0 && mineCompleted.length === 0 ? (
        <div className="rounded-2xl bg-[var(--surface)] border border-[var(--line)] p-12 text-center">
          <InboxIcon className="mx-auto text-[var(--pine-line)] mb-3" size={30} strokeWidth={1.4} />
          <p className="text-[var(--ink3)] max-w-sm mx-auto leading-relaxed">All clear. Nothing's waiting on you right now. Capture a thought whenever one arrives.</p>
        </div>
      ) : (
        <div className="space-y-10">
          {newCount > 0 && (
            <button
              type="button"
              onClick={openHandedItems}
              aria-label={newCount === 1 ? "View item handed to you" : "View items handed to you"}
              className="w-full flex items-center gap-3 rounded-2xl bg-[var(--surface2)] border border-[var(--line2)] px-5 py-3.5 -mt-2 text-left transition-all duration-200 hover:border-[var(--pine-line)] hover:bg-[var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pine-line)]"
              data-testid="new-items-summary"
            >
              <span className="h-2 w-2 rounded-full bg-[var(--pine)] shrink-0" />
              <p className="text-sm text-[var(--ink2)]">
                <span className="font-semibold">
                  {partnerName || "Your partner"} handed you {newCount} {newCount === 1 ? "item" : "items"}.
                </span>{" "}
                <span className="text-[var(--ink3)]">{newCount === 1 ? "It’s yours now." : "They’re yours now."}</span>
              </p>
            </button>
          )}
          <CollapsibleSection id="today" title="Today" count={todayItems.length || null} hint="What matters now." collapsible={false}>
            {todayItems.length > 0 ? (
              <div className="space-y-3">
                {todayItems.map((e) => <EntryRow key={e.id} entry={e} onChange={handleChange} onCueApply={handleCue} due={dueFor(e)} isNew={e.unseen} onView={markItemSeen} onOpen={setDetailId} onDelete={handleDelete} onHandoff={openHandoff} snoozable />)}
              </div>
            ) : (
              <div className="rounded-2xl bg-[var(--surface)] border border-[var(--line)] p-8 text-center" data-testid="inbox-today-empty">
                <p className="text-[var(--ink3)]">Nothing due today. You're clear.</p>
              </div>
            )}
          </CollapsibleSection>

          {handedItems.length > 0 && (
            <CollapsibleSection id="handed" title={handedLabel} count={handedItems.length} hint="Just came your way." collapsible defaultOpen accent="slate">
              <div className="space-y-3">
                {handedItems.map((e) => <EntryRow key={e.id} entry={e} onChange={handleChange} onCueApply={handleCue} due={dueFor(e)} isNew={e.unseen} onView={markItemSeen} onOpen={setDetailId} onDelete={handleDelete} onHandoff={openHandoff} handed />)}
              </div>
            </CollapsibleSection>
          )}

          {laterItems.length > 0 && (
            <CollapsibleSection id="later" title="Held for later" count={laterItems.length} hint="Quietly held until you're ready." collapsible defaultOpen={false}>
              <div className="space-y-3">
                {laterItems.map((e) => <EntryRow key={e.id} entry={e} onChange={handleChange} onCueApply={handleCue} isNew={e.unseen} onView={markItemSeen} onOpen={setDetailId} onDelete={handleDelete} onHandoff={openHandoff} />)}
              </div>
            </CollapsibleSection>
          )}

          {mineCompleted.length > 0 && (
            <section data-testid="inbox-section-completed">
              <h2 className="text-xl font-medium text-[var(--ink)] mb-4">Done</h2>
              <div className="space-y-3">
                {mineCompleted.map((e) => (
                  <EntryRow key={e.id} entry={e} onChange={handleChange} onCueApply={handleCue} onOpen={setDetailId} onDelete={handleDelete} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <ItemDetailSheet
        entry={entries.find((e) => e.id === detailId) || null}
        open={!!detailId}
        initialHandoff={handoffInit}
        onOpenChange={(o) => { if (!o) { setDetailId(null); setHandoffInit(null); } }}
        onChange={handleChange}
        onDelete={handleDelete}
      />
    </div>
  );
}
